import React from "react";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import type { DailySalesDetail, SalesTabType } from "types/BusinessDashboard";
import type { MonthlyParams, SalesDetailParams } from "api/businessDashboard";
import {
  useSalesDetailQuery,
  useTargetDataQuery,
} from "queries/Business/Dashboard/useDashboardQueries";
import { ChartSkeleton } from "../chart-ui-kit";
import { RealChart } from "./real-chart";

dayjs.locale("ko");

export const TotalSalesChart = ({
  currentDate,
  detailApiParams,
  targetApiParams,
  currentInnerTab,
}: {
  currentDate: dayjs.Dayjs;
  detailApiParams: SalesDetailParams;
  targetApiParams: MonthlyParams;
  currentInnerTab: SalesTabType;
}) => {
  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useSalesDetailQuery(detailApiParams);
  const {
    data: targetData,
    isLoading: isTargetLoading,
    isError: isTargetError,
  } = useTargetDataQuery(targetApiParams, {
    enabled: true,
  });

  const isLoading = isDetailLoading || isTargetLoading;
  const isError = isDetailError || isTargetError;

  const { charts, referenceLines } = (() => {
    if (!detailData || !targetData) {
      return {
        charts: { daily: [], cumulative: [], avgPrice: [] },
        referenceLines: { target: undefined, bep: undefined },
      };
    }

    const dailyData = detailData.data.map((d: DailySalesDetail) => ({
      day: dayjs(d.referenceDate).date(),
      value: d.dailyRevenue.amount ?? 0,
      date: dayjs(d.referenceDate).format("YY.MM.DD"),
      changeRatePrevMonth: d.dailyRevenue.changeRateComparedToPreviousMonth,
    }));

    const cumulativeData = detailData.data.map((d: DailySalesDetail) => ({
      day: dayjs(d.referenceDate).date(),
      value: d.cumulativeRevenue.amount ?? 0,
      date: dayjs(d.referenceDate).format("YY.MM.DD"),
      changeRatePrevMonth:
        d.cumulativeRevenue.changeRateComparedToPreviousMonth,
      target: d.cumulativeRevenue.targetRevenue ?? null,
      bep: d.cumulativeRevenue.breakEvenPoint ?? null,
    }));

    const monthlyReferenceLines = (() => {
      const lastDayData = detailData.data[detailData.data.length - 1];
      if (!lastDayData) {
        return { target: undefined, bep: undefined };
      }
      return {
        target: lastDayData.dailyRevenue.targetRevenue,
        bep: lastDayData.dailyRevenue.breakEvenPoint,
      };
    })();

    const avgPriceData = detailData.data.map((d: DailySalesDetail) => ({
      day: dayjs(d.referenceDate).date(),
      value: d.averageSellingPrice.amount ?? 0,
      date: dayjs(d.referenceDate).format("YY.MM.DD"),
      changeRatePrevMonth:
        d.averageSellingPrice.changeRateComparedToPreviousMonth,
    }));

    return {
      charts: {
        daily: dailyData,
        cumulative: cumulativeData,
        avgPrice: avgPriceData,
      },
      referenceLines: monthlyReferenceLines,
    };
  })();

  if (isLoading) return <ChartSkeleton title="총 매출" />;
  if (isError) return <></>;

  // 1. 데이터 포인트 수
  const numDataPoints = charts.daily.length;
  // 2. 포인트당 원하는 width(px)
  const widthPerDataPoint = 32;
  // 3. 전체 차트 width 계산
  const chartWidth = Math.max(numDataPoints * widthPerDataPoint, 600);

  return (
    <RealChart
      charts={charts}
      chartWidth={chartWidth}
      referenceLines={referenceLines}
      currentDate={currentDate}
      currentInnerTab={currentInnerTab}
    />
  );
};
